/**
 * Utility functions for generating default branding (logo/favicon) based on organization domain/name
 */

/**
 * Extracts subdomain from hostname
 * Example: "client.doaz.ai" -> "client"
 * Example: "subdomain.example.com" -> "subdomain"
 */
export const getSubdomainFromHostname = (hostname: string): string | null => {
  const parts = hostname.split(".");
  if (parts.length >= 2) {
    // Return the first part (subdomain)
    return parts[0];
  }
  return null;
};

/**
 * Gets the first letter of a domain/subdomain for favicon generation
 */
export const getFirstLetterFromDomain = (domain: string): string => {
  if (!domain) return "D"; // Default to "D" for Doaz
  
  // Extract subdomain if full domain is provided
  const subdomain = getSubdomainFromHostname(domain) || domain;
  
  // Get first letter, uppercase it
  const firstLetter = subdomain.charAt(0).toUpperCase();
  
  // If it's not a letter, return "D" as default
  return /[A-Za-z]/.test(firstLetter) ? firstLetter : "D";
};

/**
 * Generates an SVG favicon data URL from the first letter of the domain
 */
export const generateFaviconFromDomain = (domain: string): string => {
  const letter = getFirstLetterFromDomain(domain);
  
  const svg = `
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="6" fill="#0E0E0E"/>
      <text x="16" y="22" font-family="Arial, sans-serif" font-size="18" font-weight="700" fill="#FFFFFF" text-anchor="middle">${letter}</text>
    </svg>
  `.trim();
  
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

/**
 * Gets organization name or subdomain for logo fallback
 * Priority: organizationName > subdomain > default
 */
export const getOrganizationDisplayName = (
  organizationName?: string | null,
  domain?: string | null
): string => {
  if (organizationName) {
    return organizationName;
  }
  
  if (domain) {
    const subdomain = getSubdomainFromHostname(domain);
    if (subdomain) {
      // Capitalize first letter of subdomain
      return subdomain.charAt(0).toUpperCase() + subdomain.slice(1);
    }
    return domain;
  }
  
  // Fallback to current hostname subdomain
  const currentSubdomain = getSubdomainFromHostname(window.location.hostname);
  if (currentSubdomain) {
    return currentSubdomain.charAt(0).toUpperCase() + currentSubdomain.slice(1);
  }
  
  return "DOAZ"; // Ultimate fallback
};
